'use client';

import React from 'react';

import { Popover } from '@components/Popover/Popover';
import { ListItem, List as MUILIST } from '@mui/material';

import { useParams, usePathname } from 'next/navigation';
import {
  clickOutsideEventListener,
  useClickOutside,
} from '@hooks/useClickOutside';
import { getNewLanguageRoute } from '@utils/getAppRoute';
import { LANGUAGE_OPTIONS } from '@components/IntlProvider/IntlProvider';
import { Mobile } from '@components/Mobile';
import { Desktop } from '@components/Desktop';
import SVGIcons from '@components/SVGIcon';
import { NoTranslateWrapper } from '@components/NoTranslateWrapper';
import { LanguageTypes } from '../../../../types/common.types';
import { Link } from '@components/Link';

import cn from './style.module.scss';

interface LanguageDropdownProps {
  options: {
    label: string;
    value: string;
  }[];
}

export function LanguageDropdown(props: LanguageDropdownProps): JSX.Element {
  const [popupVisible, setVisible] = React.useState(false);
  const popoverContentRef = React.useRef<HTMLDivElement | null>(null);
  const selectInputRef = React.useRef<HTMLDivElement | null>(null);
  const pathname = usePathname();
  const { lang } = useParams();

  const handleClickOutside = React.useCallback(
    (event: MouseEvent) => {
      if (selectInputRef?.current?.contains(event.target as Node)) return;
      if (popupVisible) setVisible(false);
    },
    [popupVisible, selectInputRef]
  );

  useClickOutside(
    popoverContentRef,
    handleClickOutside as clickOutsideEventListener
  );

  const handleClick = () => {
    setVisible(true);
  };

  return (
    <div ref={popoverContentRef} className={cn.listItemContent}>
      <Popover
        visible={popupVisible}
        className={cn.optionsWrapper}
        enablePortal
        content={
          <>
            <MUILIST className={cn.languageBlock}>
              {props.options.map((item) => (
                <ListItem
                  style={{ backgroundColor: '#ffffff' }}
                  className={cn.languageBlock__item}
                  key={item.value}
                >
                  <Link
                    url={getNewLanguageRoute(
                      pathname,
                      item.value as LanguageTypes
                    )}
                  >
                    <Desktop>
                      <NoTranslateWrapper>
                        <span className={cn.desktop}>{item.label}</span>
                      </NoTranslateWrapper>
                    </Desktop>
                    <Mobile>
                      <NoTranslateWrapper>
                        <span className={cn.desktop}>{item.value}</span>
                      </NoTranslateWrapper>
                    </Mobile>
                  </Link>
                </ListItem>
              ))}
            </MUILIST>
          </>
        }
      >
        <div ref={selectInputRef} className={cn.wrapper} onClick={handleClick}>
          <div className={cn.languageElement}>
            <Desktop>
              <NoTranslateWrapper>
                <span>
                  {LANGUAGE_OPTIONS.find(({ value }) => value === lang)
                    ?.label ?? LANGUAGE_OPTIONS[0].label}
                </span>
              </NoTranslateWrapper>
            </Desktop>
            <Mobile>
              <NoTranslateWrapper>
                <span>{lang}</span>
              </NoTranslateWrapper>
            </Mobile>
            <span>
              <SVGIcons type="rightarrow-svg" />
            </span>
          </div>
        </div>
      </Popover>
    </div>
  );
}
